import React, { useEffect, useState } from "react";
import TrailGraph from "./TrailGraph";

function SampleMonitor2({ fguid, vehicle }) {
  const [latestData, setLatestData] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [fuelHistory, setFuelHistory] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  const url = "https://icloud.assetscontrols.com:3443/OpenApi/LBS";
  const payload = {
    FAction: "QueryLBSMonitorListByFGUIDs",
    FTokenID: "3585535a-137c-499e-82ad-af9feff7d8e7",
    FGUIDs: fguid,
    FType: 2,
  };
  const headers = {
    "Content-Type": "application/json",
  };

  // Convert raw fuel value (0-5000) to percentage (0-100)
  const getFuelPercentage = (rawValue) => {
    const percentage = (rawValue / 5000) * 100;
    return Math.min(100, Math.max(0, percentage)); // Ensure it stays between 0-100
  };
  // In SampleMonitor2.js
  const fetchData = () => {
    setIsUpdating(true);
    fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.Result === 200 && data.FObject && data.FObject.length > 0) {
          const mostRecentData = data.FObject[0];
          setLatestData(mostRecentData);

          // Update history with the new percentage
          setFuelHistory((prev) => {
            const newHistory = [
              ...prev,
              getFuelPercentage(mostRecentData.FFuelValue1),
            ];
            // Limit history to a reasonable number of points (e.g., 100)
            return newHistory.slice(-100);
          });

          setError(null);
        } else {
          setError(data.Message || "No data available");
        }
        setIsUpdating(false);
      })
      .catch((err) => {
        setError("Error occurred: " + err.message);
        setIsUpdating(false);
      });
  };

  const startTracking = () => {
    if (!isTracking) {
      // Initial call
      fetchData();
      // Start polling every 2 seconds
      const id = setInterval(fetchData, 2000);
      setIntervalId(id);
      setIsTracking(true);
    }
  };

  const stopTracking = () => {
    if (isTracking && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
      setIsTracking(false);
    }
  };

  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  if (!latestData && !isTracking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-4 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-6">
            Vehicle Monitoring Dashboard
          </h2>
          <button
            onClick={startTracking}
            className=" moButtons bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg transition-colors"
          >
            Start Tracking
          </button>
        </div>
      </div>
    );
  }

  // Get current fuel percentage
  const currentFuelPercentage = latestData
    ? getFuelPercentage(latestData.FFuelValue1)
    : 0;

  // Determine fuel status for color coding
  const fuelStatus =
    currentFuelPercentage < 20
      ? "critical"
      : currentFuelPercentage < 50
      ? "warning"
      : "normal";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div>Data for vehicle : {vehicle}</div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
            Vehicle Monitoring Dashboard
          </h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <span
                className={`h-3 w-3 rounded-full mr-2 ${
                  isUpdating
                    ? "bg-yellow-500 animate-pulse"
                    : isTracking
                    ? "bg-green-500"
                    : "bg-red-500"
                }`}
              ></span>
              <span className="text-sm text-gray-600">
                {isUpdating
                  ? "Updating..."
                  : isTracking
                  ? "Tracking Active"
                  : "Tracking Stopped"}
              </span>
            </div>
            <div className="flex space-x-2">
              {isTracking ? (
                <button
                  onClick={stopTracking}
                  className="moButtons bg-red-500 hover:bg-red-600 text-white text-sm font-medium py-1 px-3 rounded transition-colors"
                >
                  End Tracking
                </button>
              ) : (
                <button
                  onClick={startTracking}
                  className="moButtons bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-1 px-3 rounded transition-colors"
                >
                  Start Tracking
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div
          className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 ${
            isUpdating ? "opacity-75 scale-95" : "opacity-100 scale-100"
          }`}
        >
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                Live Vehicle Data
              </h3>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                {isTracking ? "Live Data" : "Data Frozen"}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Location Card */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-700">Location</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Longitude</span>
                    <span className="font-mono text-gray-800">
                      {latestData?.FLongitude || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Latitude</span>
                    <span className="font-mono text-gray-800">
                      {latestData?.FLatitude || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Fuel Card */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg mr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-orange-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                      />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-700">Fuel Status</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Level</span>
                    <div className="flex items-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                          fuelStatus === "critical"
                            ? "bg-red-100 text-red-800"
                            : fuelStatus === "warning"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {currentFuelPercentage.toFixed(1)}%
                      </span>
                      <span className="text-xs text-gray-500">
                        ({latestData?.FFuelValue1 || 0}/5000)
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e5e7eb", // bg-gray-200
                      borderRadius: "9999px", // rounded-full
                      height: "0.625rem", // h-2.5 (2.5 * 0.25rem = 0.625rem)
                    }}
                  >
                    <div
                      style={{
                        height: "0.625rem",
                        borderRadius: "9999px",
                        backgroundColor:
                          fuelStatus === "critical"
                            ? "#ef4444" // bg-red-500
                            : fuelStatus === "warning"
                            ? "#eab308"
                            : "#22c55e",
                        width: `${currentFuelPercentage}%`,
                        transition: "width 0.3s ease",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Updated */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Last updated:{" "}
                {latestData
                  ? new Date(latestData.FGPSTime).toLocaleString()
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>
        <TrailGraph fuelHistory={fuelHistory} />
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>
            {isTracking
              ? "Data is updating every 2 seconds."
              : "Tracking is currently stopped."}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SampleMonitor2;
